import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Repeat2, Bookmark, BadgeCheck, Sparkles, Music2, ExternalLink, Play, Pause, ChevronLeft, ChevronRight, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { stageApi, compact, timeAgo } from "@/lib/stageApi";

export function CreatorAvatar({ author, size = 40, className = "" }) {
  const initials = (author?.display_name || author?.handle || "?").slice(0, 1).toUpperCase();
  return author?.avatar_url ? (
    <img src={author.avatar_url} alt="" width={size} height={size} loading="lazy" className={`shrink-0 rounded-full object-cover ${className}`} style={{ width: size, height: size }} />
  ) : (
    <div className={`flex shrink-0 items-center justify-center rounded-full bg-muted font-bold text-muted-foreground ${className}`} style={{ width: size, height: size, fontSize: size / 2.4 }}>
      {initials}
    </div>
  );
}

export function VerifiedMark({ author, size = 14 }) {
  if (author?.is_verified || author?.is_verified_creator) return <BadgeCheck size={size} style={{ color: "var(--fable)" }} className="shrink-0" aria-label="Verified creator" />;
  return null;
}

export function AiTag({ author }) {
  if (!(author?.is_ai || author?.is_ai_creator)) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700" title="Posts by this creator are generated by AI">
      <Sparkles size={10} /> AI creator
    </span>
  );
}

// Instagram sizing: the media box takes the first photo / clip's own shape (kept between 1.91:1 and 4:5),
// every item fills it edge to edge — no letterbox bars, no blurred filler. Several items = a swipeable carousel.
const IG_MIN = 1 / 1.91;
const IG_MAX = 1.25;
function boxRatio(items) {
  const m = items.find((x) => x.width && x.height);
  if (!m) return items.some((x) => x.type === "video") ? 1.25 : 1;
  return Math.max(IG_MIN, Math.min(IG_MAX, m.height / m.width));
}

function MediaSlide({ m, active }) {
  const ref = React.useRef(null);
  const [muted, setMuted] = React.useState(true);
  React.useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (!active) { v.pause(); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting && e.intersectionRatio > 0.6) v.play().catch(() => {}); else v.pause(); }, { threshold: [0, 0.6, 1] });
    io.observe(v);
    return () => io.disconnect();
  }, [active]);
  if (m.type === "video") {
    return (
      <div className="relative h-full w-full">
        <video ref={ref} src={m.url} poster={m.poster || undefined} muted={muted} loop playsInline preload="metadata"
          className="h-full w-full object-cover" onClick={(e) => (e.currentTarget.paused ? e.currentTarget.play() : e.currentTarget.pause())} />
        <button type="button" onClick={() => setMuted((x) => !x)} aria-label={muted ? "Unmute" : "Mute"}
          className="absolute bottom-3 right-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-white">
          {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
        </button>
      </div>
    );
  }
  return <img src={m.url} alt={m.alt || ""} loading="lazy" draggable={false} className="h-full w-full select-none object-cover" />;
}

function MediaGrid({ media = [] }) {
  const items = media.filter((m) => m && m.url);
  const [page, setPage] = React.useState(0);
  const start = React.useRef(null);
  if (!items.length) return null;
  const n = items.length;
  const go = (p) => setPage(Math.max(0, Math.min(n - 1, p)));
  const ratio = boxRatio(items);
  return (
    <div className="group relative mt-3 overflow-hidden rounded-2xl bg-black" style={{ aspectRatio: String(1 / ratio), boxShadow: "0 0 0 1.5px var(--fable, #E0007A)" }}
      onTouchStart={(e) => { start.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => { if (start.current == null) return; const dx = e.changedTouches[0].clientX - start.current; if (Math.abs(dx) > 40) go(page + (dx < 0 ? 1 : -1)); start.current = null; }}
      data-testid="post-media">
      <div className="flex h-full transition-transform duration-300 ease-out" style={{ width: n * 100 + "%", transform: "translateX(-" + (page * 100) / n + "%)" }}>
        {items.map((m, i) => (
          <div key={m.url + i} className="h-full" style={{ width: 100 / n + "%" }}><MediaSlide m={m} active={i === page} /></div>
        ))}
      </div>
      {n > 1 && (
        <>
          {page > 0 && <button type="button" aria-label="Previous" onClick={() => go(page - 1)} className="absolute left-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-black opacity-0 shadow transition group-hover:opacity-100"><ChevronLeft size={18} /></button>}
          {page < n - 1 && <button type="button" aria-label="Next" onClick={() => go(page + 1)} className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-black opacity-0 shadow transition group-hover:opacity-100"><ChevronRight size={18} /></button>}
          <div className="pointer-events-none absolute bottom-2.5 left-0 right-0 flex justify-center gap-1.5">
            {items.map((_, i) => <span key={i} className={"h-1.5 w-1.5 rounded-full " + (i === page ? "bg-white" : "bg-white/45")} />)}
          </div>
          <span className="pointer-events-none absolute right-3 top-3 rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-semibold text-white">{page + 1}/{n}</span>
        </>
      )}
    </div>
  );
}

function Poll({ poll }) {
  if (!poll || !Array.isArray(poll.options)) return null;
  const total = poll.options.reduce((s, o) => s + (o.votes || 0), 0) || 0;
  return (
    <div className="mt-3 space-y-1.5" data-testid="post-poll">
      {poll.options.map((o, i) => {
        const pct = total ? Math.round(((o.votes || 0) / total) * 100) : 0;
        return (
          <div key={i} className="relative overflow-hidden rounded-xl border border-border px-3 py-2 text-sm">
            <span className="absolute inset-y-0 left-0 bg-primary/10" style={{ width: `${pct}%` }} />
            <span className="relative flex justify-between"><span>{o.text || o.label}</span><span className="font-mono-num text-muted-foreground">{pct}%</span></span>
          </div>
        );
      })}
      <p className="text-xs text-muted-foreground">{compact(total)} votes · vote in the app</p>
    </div>
  );
}

const LOGO = {
  spotify: { d: "M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z", color: "#1DB954" },
  apple_music: { d: "M23.994 6.124a9.23 9.23 0 00-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043a5.022 5.022 0 00-1.877-.726 10.496 10.496 0 00-1.564-.15c-.04-.003-.083-.01-.124-.013H5.986c-.152.01-.303.017-.455.026-.747.043-1.49.123-2.193.4-1.336.53-2.3 1.452-2.865 2.78-.192.448-.292.925-.363 1.408-.056.392-.088.785-.1 1.18 0 .032-.007.062-.01.093v12.223c.01.14.017.283.027.424.05.815.154 1.624.497 2.373.65 1.42 1.738 2.353 3.234 2.801.42.127.856.187 1.293.228.555.053 1.11.06 1.667.06h11.03a12.5 12.5 0 001.57-.1c.822-.106 1.596-.35 2.295-.81a5.046 5.046 0 001.88-2.207c.186-.42.293-.87.37-1.324.113-.675.138-1.358.137-2.04-.002-3.8 0-7.595-.003-11.393zm-6.423 3.99v5.712c0 .417-.058.827-.244 1.206-.29.59-.76.962-1.388 1.14-.35.1-.706.157-1.07.173-.95.045-1.773-.6-1.943-1.536a1.88 1.88 0 011.038-2.022c.323-.16.67-.25 1.018-.324.378-.082.758-.153 1.134-.24.274-.063.457-.23.51-.516a.904.904 0 00.02-.193c0-1.815 0-3.63-.002-5.443a.725.725 0 00-.026-.185c-.04-.15-.15-.243-.304-.234-.16.01-.318.035-.475.066-.76.15-1.52.303-2.28.456l-2.325.47-1.374.278c-.016.003-.032.01-.048.013-.277.077-.377.203-.39.49-.002.042 0 .086 0 .13-.002 2.602 0 5.204-.003 7.805 0 .42-.047.836-.215 1.227-.278.64-.77 1.04-1.434 1.233-.35.1-.71.16-1.075.172-.96.036-1.755-.6-1.92-1.544-.14-.812.23-1.685 1.154-2.075.357-.15.73-.232 1.108-.31.287-.06.575-.116.86-.177.383-.083.583-.323.6-.714v-.15c0-2.96 0-5.922.002-8.882 0-.123.013-.25.042-.37.07-.285.273-.448.546-.518.255-.066.515-.112.774-.165.733-.15 1.466-.296 2.2-.444l2.27-.46c.67-.134 1.34-.27 2.01-.403.22-.043.442-.088.663-.106.31-.025.523.17.554.482.008.073.012.148.012.223.002 1.91.002 3.822 0 5.732z", color: "#FA243C" },
  youtube: { d: "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z", color: "#FF0000" },
};
let nowPlaying = null; // one preview at a time across the feed

// Music credits: title, an official 30-second Apple Music preview, and logo-only links (Spotify, Apple Music, YouTube).
function TrackLinks({ links = [], sound }) {
  const groups = (links || []).filter((g) => g && g.kind !== "news").slice(0, 3);
  if (!groups.length && !sound) return null;
  if (!groups.length) {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-xl bg-muted/60 px-3 py-2 text-xs">
        <Music2 size={14} className="text-muted-foreground" /><span className="truncate font-medium">{sound?.title || sound?.name}</span>
      </div>
    );
  }
  return (
    <div className="mt-3 divide-y divide-border rounded-xl bg-muted/60 px-3 py-1 text-xs" data-testid="post-listen">
      {groups.map((g, i) => <TrackRow key={(g.title || "") + i} g={g} />)}
    </div>
  );
}

const PLATFORMS = [
  { key: "apple_music", label: "Apple Music", logo: "apple_music" },
  { key: "spotify", label: "Spotify", logo: "spotify" },
  { key: "youtube_music", label: "YouTube Music", logo: "youtube" },
  { key: "deezer", label: "Deezer", color: "#A238FF" },
];

function TrackRow({ g }) {
  const [state, setState] = useState("idle");
  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState({});
  const audio = React.useRef(null);
  const artist = g.artist || String(g.title || "").split(/\s[\u2013\u2014-]\s|\s\u00b7\s/)[0];
  React.useEffect(() => () => { if (audio.current) { audio.current.pause(); if (nowPlaying === audio.current) nowPlaying = null; } }, []);

  const q = encodeURIComponent(g.title || "");
  const fromPost = {};
  (g.items || []).forEach((it) => { fromPost[it.service === "youtube" ? "youtube_music" : it.service] = it.url; });
  const fallback = { apple_music: "https://music.apple.com/gb/search?term=" + q, spotify: "https://open.spotify.com/search/" + q,
    youtube_music: "https://music.youtube.com/search?q=" + q, deezer: "https://www.deezer.com/search/" + q };
  const platformUrl = (k) => fromPost[k] || links[k] || fallback[k];
  const toggle = async () => {
    if (state === "playing") { audio.current?.pause(); setState("idle"); return; }
    setState("loading");
    try {
      if (!audio.current) {
        const r = await fetch((process.env.REACT_APP_BACKEND_URL || "") + "/api/music/preview?artist=" + encodeURIComponent(artist) + (g.track ? "&track=" + encodeURIComponent(g.track) : "")).then((x) => x.json());
        if (r.links) setLinks(r.links);
        if (!r.preview_url) { setState("none"); return; }
        const el = new Audio(r.preview_url);
        el.onended = () => setState("idle");
        el.onpause = () => setState((s) => (s === "playing" ? "idle" : s));
        audio.current = el;
      }
      if (nowPlaying && nowPlaying !== audio.current) nowPlaying.pause();
      audio.current.currentTime = 0;
      await audio.current.play();
      nowPlaying = audio.current;
      setState("playing");
    } catch { setState("none"); }
  };
  return (
    <div className="relative flex items-center gap-2 py-1.5">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-label={"Listen to " + g.title} aria-expanded={open}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary/20" data-testid="post-listen-music">
        <Music2 size={14} />
      </button>
      <span className="min-w-0 flex-1 truncate font-medium">{g.title}</span>
      {artist && state !== "none" ? (
        <button type="button" onClick={toggle} aria-label={(state === "playing" ? "Pause preview of " : "Play a 30-second preview of ") + g.title}
          className={"inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors " + (state === "playing" ? "bg-[#FA243C] text-white" : "border border-border bg-background hover:bg-muted")}
          data-testid="post-listen-preview">
          {state === "loading" ? <span className="text-[10px]">{"\u2026"}</span> : state === "playing" ? <Pause size={12} /> : <Play size={12} className="ml-0.5" />}
        </button>
      ) : null}
      {open ? (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="absolute bottom-full left-0 z-50 mb-2 w-[280px] rounded-2xl border border-border bg-popover p-3 shadow-xl" role="dialog" aria-label={"Listen to " + g.title}>
            <p className="truncate text-sm font-bold">{g.title}</p>
            <p className="text-[11px] text-muted-foreground">Listen on</p>
            <div className="mt-2 grid grid-cols-4 gap-2">
              {PLATFORMS.map((p) => (
                <a key={p.key} href={platformUrl(p.key)} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}
                  className="flex flex-col items-center gap-1 rounded-xl p-1.5 hover:bg-muted" aria-label={"Open on " + p.label} data-testid={"post-listen-" + p.key}>
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-background">
                    {LOGO[p.logo] ? <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d={LOGO[p.logo].d} fill={LOGO[p.logo].color} /></svg> : <Music2 size={18} style={{ color: p.color }} />}
                  </span>
                  <span className="w-full truncate text-center text-[10px] font-semibold">{p.label}</span>
                </a>
              ))}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function Stat({ icon: Icon, n, active, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium transition-colors hover:bg-muted ${active ? "text-[var(--fable)]" : "text-muted-foreground"}`}
    >
      <Icon size={16} fill={active ? "currentColor" : "none"} /> <span className="font-mono-num">{compact(n)}</span>
    </button>
  );
}

/** One Pulse post. Like/save work for signed-in members; everything else is read-only on the web. */
export function PostCard({ post, compactMode = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const original = post.repost_of && post.original ? post.original : null;
  const p = original || post;
  const a = p.author || {};
  const [liked, setLiked] = useState(!!p.viewer?.liked);
  const [likes, setLikes] = useState(p.stats?.likes || 0);
  const [saved, setSaved] = useState(!!p.viewer?.saved);

  const needLogin = () => {
    toast("Sign in to react to posts.");
    navigate("/login", { state: { from: window.location.pathname + window.location.search } });
  };
  if (post.repost_of && !post.original) return null; // the original was removed; nothing to show
  const toggleLike = async () => {
    if (!user) return needLogin();
    const next = !liked;
    setLiked(next);
    setLikes((n) => Math.max(0, n + (next ? 1 : -1)));
    try {
      await stageApi.like(p.id, next);
    } catch (e) {
      setLiked(!next);
      setLikes((n) => Math.max(0, n + (next ? -1 : 1)));
      toast.error(e.message || "Couldn't update that.");
    }
  };
  const toggleSave = async () => {
    if (!user) return needLogin();
    const next = !saved;
    setSaved(next);
    try {
      await stageApi.save(p.id, next);
      toast.success(next ? "Saved" : "Removed from saved");
    } catch (e) {
      setSaved(!next);
      toast.error(e.message || "Couldn't update that.");
    }
  };

  const hasMedia = Array.isArray(p.media) && p.media.some((m) => m && m.url);
  return (
    <article className={hasMedia ? "py-1" : "rounded-2xl px-4 py-3"} style={hasMedia ? undefined : { boxShadow: "0 0 0 1.5px var(--fable, #E0007A)" }} data-testid={`post-${post.id}`}>
      {original && (
        <p className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Repeat2 size={13} /> <Link to={`/@${post.author?.handle}`} className="font-medium hover:underline">{post.author?.display_name || post.author?.handle}</Link> reposted
        </p>
      )}
      <header className="flex items-start gap-3">
        <Link to={`/@${a.handle}`} className="shrink-0"><CreatorAvatar author={a} size={compactMode ? 36 : 42} /></Link>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <Link to={`/@${a.handle}`} className="truncate font-semibold hover:underline">{a.display_name || a.handle}</Link>
            <VerifiedMark author={a} />
            <AiTag author={a} />
            <span className="text-xs text-muted-foreground">@{a.handle} · {timeAgo(p.created_at)}</span>
          </div>
          {Array.isArray(a.categories) && a.categories.length > 0 && <p className="text-xs capitalize text-muted-foreground">{a.categories.slice(0, 3).join(" · ")}</p>}
        </div>
      </header>
      {p.text && <p className="mt-3 whitespace-pre-wrap text-[15px] leading-relaxed">{p.text}</p>}
      {Array.isArray(p.tags) && p.tags.length > 0 && (
        <p className="mt-2 flex flex-wrap gap-1.5">
          {p.tags.slice(0, 6).map((t) => (
            <Link key={t} to={`/stage/feed?q=${encodeURIComponent("#" + t)}`} className="text-xs font-medium text-primary hover:underline">#{t}</Link>
          ))}
        </p>
      )}
      <MediaGrid media={p.media} />
      <Poll poll={p.poll} />
      <TrackLinks links={p.links} sound={p.sound} />
      {Array.isArray(p.products) && p.products.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {p.products.slice(0, 3).map((pr) => (
            <span key={pr.id} className={`inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs font-medium ${pr.sold_out ? "opacity-60" : ""}`} title="Shop in the Fable + app">
              {pr.cover && <img src={pr.cover} alt="" className="h-4 w-4 rounded object-cover" />}
              {pr.title}
              {pr.price_gbp != null && <span className="font-mono-num text-muted-foreground">£{Number(pr.price_gbp).toFixed(2)}</span>}
              {pr.sold_out && <span className="text-muted-foreground">· sold out</span>}
            </span>
          ))}
        </div>
      )}
      <footer className="mt-2 flex items-center gap-1">
        <Stat icon={Heart} n={likes} active={liked} onClick={toggleLike} label={liked ? "Unlike" : "Like"} testid={`like-${p.id}`} />
        <Stat icon={MessageCircle} n={p.stats?.comments} onClick={() => navigate(`/stage/post/${p.id}`)} label="Comments" />
        <Stat icon={Repeat2} n={p.stats?.reposts} onClick={() => toast("Reposting lives in the app for now.")} label="Reposts" />
        <span className="flex-1" />
        <Stat icon={Bookmark} n={saved ? 1 : 0} active={saved} onClick={toggleSave} label={saved ? "Unsave" : "Save"} />
      </footer>
    </article>
  );
}

/** Square tile for the Explore / profile grids. */
export function PostTile({ post }) {
  const m = (post.media || []).find((x) => x && x.url);
  return (
    <Link to={`/stage/post/${post.id}`} className="group relative block aspect-square overflow-hidden rounded-xl bg-muted" data-testid={`tile-${post.id}`}>
      {m ? (
        m.type === "video" ? (
          <>
            <video src={m.url} poster={m.poster || undefined} preload="none" muted playsInline className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <span className="absolute right-2 top-2 text-white drop-shadow"><Play size={16} fill="white" /></span>
          </>
        ) : (
          <img src={m.url} alt={m.alt || ""} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        )
      ) : (
        <div className="flex h-full w-full items-start p-3 text-sm leading-snug text-foreground/80" style={{ background: "linear-gradient(135deg,#FFE1F0,#F3E8FF)" }}>
          <span className="line-clamp-6">{post.text}</span>
        </div>
      )}
      <span className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-gradient-to-t from-black/60 to-transparent px-3 pb-2 pt-6 text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
        <span className="inline-flex items-center gap-1"><Heart size={12} fill="white" /> {compact(post.stats?.likes)}</span>
        <span className="inline-flex items-center gap-1"><MessageCircle size={12} fill="white" /> {compact(post.stats?.comments)}</span>
      </span>
    </Link>
  );
}
